import { safeExecute, } from "@opendaw/lib-std";
import { Box, NoPointers, PointerField, StringField, BooleanField, Int32Field, Float32Field, ArrayField, } from "@opendaw/lib-box";
import { NeonLine } from "./NeonLine";
import { NeonEnvelope } from "./NeonEnvelope";
import { NeonVibrato } from "./NeonVibrato";
import { Pointers } from "@opendaw/studio-enums";
export class NeonDeviceBox extends Box {
    static create(graph, uuid, constructor) {
        return graph.stageBox(new NeonDeviceBox({
            uuid,
            graph,
            name: "NeonDeviceBox",
            pointerRules: {
                accepts: [
                    Pointers.Device,
                    Pointers.Selection,
                    Pointers.MetaData,
                    Pointers.SideChain,
                ],
                mandatory: false,
                exclusive: false,
            },
            resource: undefined,
            ephemeral: false,
        }), constructor);
    }
    static ClassName = "NeonDeviceBox";
    static Resource = undefined;
    static Ephemeral = false;
    static Tags = Object.freeze({
        type: "device",
        deviceType: "instrument",
        content: "notes",
    });
    constructor(construct) {
        super(construct);
    }
    accept(visitor) {
        return safeExecute(visitor.visitNeonDeviceBox, this);
    }
    get tags() {
        return NeonDeviceBox.Tags;
    }
    get host() {
        return this.getField(1);
    }
    get label() {
        return this.getField(2);
    }
    get icon() {
        return this.getField(3);
    }
    get enabled() {
        return this.getField(4);
    }
    get minimized() {
        return this.getField(5);
    }
    get lineSelect() {
        return this.getField(10);
    }
    get modulation() {
        return this.getField(11);
    }
    get octave() {
        return this.getField(12);
    }
    get detune() {
        return this.getField(13);
    }
    get glideTime() {
        return this.getField(15);
    }
    get tune() {
        return this.getField(16);
    }
    get voicingMode() {
        return this.getField(17);
    }
    get vibrato() {
        return this.getField(20);
    }
    get lines() {
        return this.getField(30);
    }
    get envelopes() {
        return this.getField(40);
    }
    initializeFields() {
        return {
            1: PointerField.create({
                parent: this,
                fieldKey: 1,
                fieldName: "host",
                deprecated: false,
                pointerRules: NoPointers,
            }, Pointers.InstrumentHost, true),
            2: StringField.create({
                parent: this,
                fieldKey: 2,
                fieldName: "label",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            3: StringField.create({
                parent: this,
                fieldKey: 3,
                fieldName: "icon",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            4: BooleanField.create({
                parent: this,
                fieldKey: 4,
                fieldName: "enabled",
                deprecated: false,
                pointerRules: NoPointers,
            }, true),
            5: BooleanField.create({
                parent: this,
                fieldKey: 5,
                fieldName: "minimized",
                deprecated: false,
                pointerRules: NoPointers,
            }, false),
            10: Int32Field.create({
                parent: this,
                fieldKey: 10,
                fieldName: "lineSelect",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { length: 4 }, "", 0),
            11: Int32Field.create({
                parent: this,
                fieldKey: 11,
                fieldName: "modulation",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { length: 3 }, "", 0),
            12: Int32Field.create({
                parent: this,
                fieldKey: 12,
                fieldName: "octave",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: -3, max: 3 }, "oct", 0),
            13: Float32Field.create({
                parent: this,
                fieldKey: 13,
                fieldName: "detune",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: -4800, max: 4800, scaling: "linear" }, "ct", 0),
            15: Float32Field.create({
                parent: this,
                fieldKey: 15,
                fieldName: "glideTime",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, "unipolar", "%", 0),
            16: Float32Field.create({
                parent: this,
                fieldKey: 16,
                fieldName: "tune",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { min: -1200, max: 1200, scaling: "linear" }, "ct", 0),
            17: Int32Field.create({
                parent: this,
                fieldKey: 17,
                fieldName: "voicingMode",
                deprecated: false,
                pointerRules: {
                    accepts: [
                        Pointers.Modulation,
                        Pointers.Automation,
                        Pointers.MIDIControl,
                    ],
                    mandatory: false,
                    exclusive: false,
                },
            }, { values: [0, 1] }, "", 1),
            20: NeonVibrato.create({
                parent: this,
                fieldKey: 20,
                fieldName: "vibrato",
                deprecated: false,
                pointerRules: NoPointers,
            }),
            30: ArrayField.create({
                parent: this,
                fieldKey: 30,
                fieldName: "lines",
                deprecated: false,
                pointerRules: NoPointers,
            }, (construct) => NeonLine.create(construct), 2),
            40: ArrayField.create({
                parent: this,
                fieldKey: 40,
                fieldName: "envelopes",
                deprecated: false,
                pointerRules: NoPointers,
            }, (construct) => NeonEnvelope.create(construct), 6),
        };
    }
}
